import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  TrashIcon,
  MinusIcon,
  PlusIcon,
  ShoppingCartIcon,
  CheckIcon,
  UserIcon,
  PhoneIcon,
  ClockIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';
import { useCart } from '../../context/CartContext';
import { useBranch } from '../../context/BranchContext';
import { useAuth } from '../../context/AuthContext';
import {
  Modal,
  Form,
  Input,
  Radio,
  DatePicker,
  TimePicker,
  Select,
  Collapse,
  List,
  Tag,
  Typography,
  Divider,
} from 'antd';
import {
  showSuccess,
  showWarning,
  showError,
  showInfo,
} from '../../utils/notificationUtils';
import type { Dayjs } from 'dayjs';
import locale from 'antd/es/date-picker/locale/vi_VN';
import { OrderRequest } from '../../types/order';
import {
  GiftOutlined,
  ShoppingOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { orderService } from '../../services/order.service';
import { useTranslation } from 'react-i18next';
import { promotionService, Promotion } from '../../services/promotion.service';
import {
  getProductImageUrl,
  handleProductImageError,
} from '../../utils/productImage';


const CartPage: React.FC = () => {
  const {
    items,
    updateItemQuantity,
    removeItem,
    discount,
    toggleSelectItem,
    selectAllItems,
    unselectAllItems,
    isAllSelected,
    selectedItems,
    selectedSubtotal,
    applyPromotion,
    activePromotion,
  } = useCart();

  const { t } = useTranslation() as { t: (key: string) => string };
  const { selectedBranch } = useBranch();
  const { isAuthenticated } = useAuth();
  const [couponCode, setCouponCode] = useState('');
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [form] = Form.useForm();
  const [type, setType] = useState<'immediate' | 'schedule' | 'pickup'>(
    'immediate'
  );
  const [receiverType, setReceiverType] = useState<'self' | 'other'>('self');
  const [orderSuccess, setOrderSuccess] = useState<any>(null);
  const { Option } = Select;
  const [expandedBoxes, setExpandedBoxes] = useState<Record<string, boolean>>(
    {}
  );
  const { Text } = Typography;
  const { Panel } = Collapse;
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loadingPromotions, setLoadingPromotions] = useState(false);

  // Load user data when modal opens
  useEffect(() => {
    if (showCheckoutForm && isAuthenticated) {
      try {
        const userData = localStorage.getItem('fruitshop_user');
        if (userData) {
          const user = JSON.parse(userData);
          // Set form default values if user data available
          form.setFieldsValue({
            name: user.name || '',
            order_phone: user.phone || '',
            phone: '', // Leave receiver phone empty as per instructions
          });
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    }
  }, [showCheckoutForm, isAuthenticated, form]);

  // Load available promotions
  useEffect(() => {
    const fetchPromotions = async () => {
      setLoadingPromotions(true);
      try {
        const data = await promotionService.getPublicPromotions();
        setPromotions(data);
      } catch (error) {
        console.error('Error fetching promotions:', error);
      } finally {
        setLoadingPromotions(false);
      }
    };

    fetchPromotions();
  }, []);

  // Toggle expansion for fruit box items

  // Handle checkout for selected items
  const handleCheckout = async () => {
    if (selectedItems.length === 0) {
      showWarning(
        t('cart.selectProductsWarningTitle'),
        t('cart.selectProductsWarningMessage')
      );
      return;
    }

    if (!selectedBranch) {
      showWarning(
        t('cart.noBranchSelectedTitle'),
        t('cart.noBranchSelectedMessage')
      );
      return;
    }

    // Show the checkout form modal
    setShowCheckoutForm(true);
  };

  // Submit checkout with customer information
  const submitCheckout = async (values: {
    name: string;
    phone: string;
    order_phone: string;
    address: string;
    type: 'immediate' | 'schedule' | 'pickup';
    deliveryDate?: Dayjs;
    deliveryTime?: Dayjs;
    deliveryNote?: string;
  }) => {
    try {
      setIsProcessing(true);

      // Format delivery date and time if scheduled or pickup
      let formattedDeliveryDateTime: string | undefined = undefined;

      if (
        (values.type === 'schedule' || values.type === 'pickup') &&
        values.deliveryDate &&
        values.deliveryTime
      ) {
        const date = values.deliveryDate.format('YYYY-MM-DD');
        const time = values.deliveryTime.format('HH:mm');
        formattedDeliveryDateTime = `${date}T${time}:00.000Z`;
      }

      // Separate items by type
      console.log('selectedItems', selectedItems);
      const variants = selectedItems.filter((item => item.type === 'multiple_variants'));
      const regularItems = selectedItems.filter(
        (item) => item.type === 'single'
      );
      const fruitBoxItems = selectedItems.filter(
        (item) => item.type === 'fruit_box'
      );

      // Map checkoutdata to OrderRequest
      const orderRequest: OrderRequest = {
        branch_id: selectedBranch?._id || '',
        items: regularItems.map((item) => ({
          product_id: item.productId,
          quantity: item.quantity,
        })),
        variants: variants.map((item) => ({
          product_id: item.productId,
          variant_id: item.variantId ?? '',
          quantity: item.quantity,
        })),
        box_templates: fruitBoxItems.map((item) => ({
          template_id: item.productId,
          quantity: item.quantity,
          selected_products: (item.selectedItems || []).map((selectedItem) => ({
            product_id: selectedItem.id,
            quantity: selectedItem.quantity,
          })),
        })),
        delivery_info: {
          // Thông tin người đặt
          order_phone: values.order_phone,
          
          // Thông tin người nhận
          receiver_name: values.name,
          receiver_phone: receiverType === 'other' ? values.phone : values.order_phone,
          
          // Thông tin giao hàng
          address: values.address,
          type: values.type,
          delivery_datetime: formattedDeliveryDateTime,
          note: values.deliveryNote || undefined,
        },
      };

      // Add promotion code if active
      if (activePromotion) {
        orderRequest.promotion_code = activePromotion.code;
      }

      console.log('Order request:', orderRequest);

      // Call the API to create the order
      const response = isAuthenticated
        ? await orderService.createOrderForUser(orderRequest)
        : await orderService.createOrderForGuest(orderRequest);

      console.log('Order response:', response);

      // Debug: Log response structure to understand the response format
      console.log('Response properties:', Object.keys(response || {}));

      // Different APIs may have different success indicators:
      // - HTTP status is typically handled by axios in the service layer
      // - Common success indicators in response body: success, status, code, data
      const isSuccess = Boolean(
        response &&
          // The response exists and either:
          // 1. Has no explicit error indicators
          ((response.error === undefined && response.errorCode === undefined) ||
            // 2. Has explicit success indicators
            response.success === true ||
            response.status === 'success' ||
            response.code === 200)
      );

      if (isSuccess) {
        // Lưu thông tin đơn hàng để hiển thị màn hình thành công
        setOrderSuccess(response);
        
        // Close the form and reset state
        setShowCheckoutForm(false);
        form.resetFields();
        setType('immediate');
        setReceiverType('self');
        setIsProcessing(false);
        unselectAllItems();
      } else {
        // Get error message from response if available
        const errorMessage =
          (response && response.message) || t('cart.orderFailedDefaultMessage');

        showError(t('cart.orderFailedTitle'), errorMessage);
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      showError(
        t('cart.orderFailedTitle'),
        t('cart.orderFailedDefaultMessage')
      );
      setIsProcessing(false);
    }
  };

  // Function to apply promotion code
  const applyPromoCode = async (code: string) => {
    if (!code.trim()) {
      showInfo('Mã giảm giá trống', 'Vui lòng nhập mã giảm giá.');
      return;
    }

    if (selectedSubtotal === 0) {
      showWarning(
        'Chưa chọn sản phẩm',
        'Vui lòng chọn ít nhất một sản phẩm để áp dụng mã.'
      );
      return;
    }

    try {
      setIsProcessing(true);
      // Get promotion validation
      const promotion = await promotionService.validatePromoCode(
        code,
        selectedSubtotal
      );

      // Check if we have minimum order amount constraint
      if (
        promotion.constraints?.minimum_order_amount > 0 &&
        selectedSubtotal < promotion.constraints.minimum_order_amount
      ) {
        showWarning(
          'Chưa đạt giá trị tối thiểu',
          `Đơn hàng cần tối thiểu ${promotion.constraints.minimum_order_amount.toLocaleString(
            'vi-VN'
          )}₫ để áp dụng mã này.`
        );
        return;
      }

      // Apply promotion to cart
      applyPromotion(promotion);

      // Show success message
      showSuccess(
        'Áp dụng mã thành công',
        `Mã ${code} đã được áp dụng thành công.`
      );
    } catch (error: any) {
      let errorMessage = 'Mã giảm giá không hợp lệ hoặc đã hết hạn';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showError('Lỗi áp dụng mã', errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  // Màn hình hoàn thành đặt hàng
  if (orderSuccess) {
    const order = orderSuccess.order;
    const deliveryInfo = order.delivery_info;
    
    return (
      <div className='container mx-auto px-4 py-8'>
        <div className='max-w-4xl mx-auto'>
          {/* Success Header */}
          <div className='text-center bg-white p-6 md:p-8 rounded-lg shadow-sm mb-6'>
            <div className='w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-green-100'>
              <svg
                className='w-8 h-8 md:w-10 md:h-10 text-green-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M5 13l4 4L19 7'
                ></path>
              </svg>
            </div>
            <h1 className='text-xl md:text-2xl font-bold text-green-600 mb-2'>
              Đã ghi nhận đơn hàng
            </h1>
            <p className='text-base md:text-lg font-medium text-gray-800 mb-2'>
              Mã đơn hàng: <span className='text-primary font-bold'>{order.order_code}</span>
            </p>
            <p className='text-sm md:text-base text-gray-600 max-w-2xl mx-auto'>
              Nhân viên Ume Fruits sẽ liên hệ anh/chị trong thời gian sớm nhất để xác nhận đơn hàng. 
              Anh/chị để ý điện thoại nhé!
            </p>
          </div>

          {/* Desktop Layout */}
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6'>
            {/* Order Summary */}
            <div className='bg-white rounded-lg shadow-sm p-4 md:p-6'>
              <h2 className='text-base md:text-lg font-bold text-gray-800 mb-4'>Thông tin đơn hàng</h2>
              
              {/* Products */}
              <div className='mb-4'>
                <h3 className='font-medium text-gray-700 mb-3'>Sản phẩm:</h3>
                <div className='space-y-2'>
                  {/* Regular items */}
                  {order.items?.map((item: any, index: number) => (
                    <div key={index} className='flex justify-between text-sm'>
                      <span className='flex-1 mr-2'>{item.quantity} x {item.product_name}</span>
                      <span className='font-medium text-primary'>{item.subtotal?.toLocaleString('vi-VN')}₫</span>
                    </div>
                  ))}
                  
                  {/* Variants */}
                  {order.variants?.map((variant: any, index: number) => (
                    <div key={index} className='flex justify-between text-sm'>
                      <span className='flex-1 mr-2'>{variant.quantity} x {variant.product_name} - {variant.variant_name}</span>
                      <span className='font-medium text-primary'>{variant.subtotal?.toLocaleString('vi-VN')}₫</span>
                    </div>
                  ))}
                  
                  {/* Box templates */}
                  {order.box_templates?.map((box: any, index: number) => (
                    <div key={index} className='flex justify-between text-sm'>
                      <span className='flex-1 mr-2'>{box.quantity} x {box.template_name}</span>
                      <span className='font-medium text-primary'>{box.subtotal?.toLocaleString('vi-VN')}₫</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className='border-t border-gray-200 pt-4'>
                <div className='flex justify-between items-center'>
                  <span className='text-base md:text-lg font-bold text-gray-800'>Tổng tiền:</span>
                  <span className='text-lg md:text-xl font-bold text-primary'>
                    {order.pricing?.total_amount?.toLocaleString('vi-VN')}₫
                  </span>
                </div>
              </div>
            </div>

            {/* Delivery Info */}
            <div className='bg-white rounded-lg shadow-sm p-4 md:p-6'>
              <h2 className='text-base md:text-lg font-bold text-gray-800 mb-4'>Thông tin nhận hàng</h2>
              <div className='space-y-3'>
                <div className='flex items-start'>
                  <span className='font-medium text-gray-700 w-20 flex-shrink-0'>Người nhận:</span>
                  <span className='text-gray-600'>{deliveryInfo.receiver_name}</span>
                </div>
                <div className='flex items-start'>
                  <span className='font-medium text-gray-700 w-20 flex-shrink-0'>SĐT:</span>
                  <span className='text-gray-600'>{deliveryInfo.receiver_phone}</span>
                </div>
                <div className='flex items-start'>
                  <span className='font-medium text-gray-700 w-20 flex-shrink-0'>Địa chỉ:</span>
                  <span className='text-gray-600'>{deliveryInfo.address}</span>
                </div>
                <div className='flex items-start'>
                  <span className='font-medium text-gray-700 w-20 flex-shrink-0'>Giao hàng:</span>
                  <span className='text-gray-600'>{
                    deliveryInfo.type === 'immediate' ? 'Giao hàng ngay' :
                    deliveryInfo.type === 'schedule' ? 'Đặt lịch giao hàng' :
                    'Nhận hàng tại cửa hàng'
                  }</span>
                </div>
                {deliveryInfo.note && (
                  <div className='flex items-start'>
                    <span className='font-medium text-gray-700 w-20 flex-shrink-0'>Ghi chú:</span>
                    <span className='text-gray-600'>{deliveryInfo.note}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Button */}
          <div className='text-center'>
            <Link
              to='/'
              className='inline-block bg-primary hover:bg-primary-dark text-white font-medium py-3 px-8 rounded-lg transition duration-300'
            >
              Về trang chủ
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className='container mx-auto px-4 py-12'>
        <div className='text-center bg-white p-8 rounded-lg shadow-sm'>
          <div className='w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-gray-100'>
            <svg
              className='w-10 h-10 text-gray-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z'
              ></path>
            </svg>
          </div>
          <h2 className='text-2xl font-bold mb-4'>
            Giỏ hàng của bạn đang trống
          </h2>
          <p className='text-gray-600 mb-6'>
            Hãy thêm sản phẩm vào giỏ hàng để tiếp tục mua sắm
          </p>
          <Link
            to='/'
            className='inline-block bg-primary hover:bg-primary-dark text-white font-medium py-2 px-6 rounded-md transition duration-300'
          >
            Tiếp tục mua sắm
          </Link>
        </div>

        {/* Suggested Products */}
        {/* <div className='mt-16'>
          <ProductGrid
            products={suggestedProducts}
            title='Có thể bạn sẽ thích'
          />
        </div> */}
      </div>
    );
  }

  return (
    <div className='container mx-auto px-4 py-4 md:py-8 pb-24 md:pb-8'>
      <h1 className='text-xl md:text-2xl font-bold mb-4 md:mb-8'>{t('cart.yourCart')}</h1>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8'>
        {/* Cart Items */}
        <div className='lg:col-span-2'>
          <div className='bg-white rounded-lg shadow-sm overflow-hidden mb-4 md:mb-6'>
            <div className='hidden sm:grid sm:grid-cols-12 bg-gray-50 p-3 md:p-4 text-sm font-medium text-gray-500'>
              <div className='sm:col-span-1 text-center'>
                <input
                  type='checkbox'
                  checked={isAllSelected}
                  onChange={() =>
                    isAllSelected ? unselectAllItems() : selectAllItems()
                  }
                  className='h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary'
                />
              </div>
              <div className='sm:col-span-5'>Sản phẩm</div>
              <div className='sm:col-span-2 text-center'>Giá</div>
              <div className='sm:col-span-2 text-center'>Số lượng</div>
              <div className='sm:col-span-2 text-center'>Tổng</div>
            </div>

            {items.map((item) => (
              <div key={item.id}>
                <div
                  className='border-t border-gray-200 p-3 md:p-4 flex items-center gap-3 md:gap-4 cursor-pointer hover:bg-gray-50'
                  onClick={() => toggleSelectItem(item.id)}
                >
                  {/* Checkbox */}
                  <div
                    className='flex-shrink-0'
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type='checkbox'
                      checked={item.selected}
                      onChange={() => toggleSelectItem(item.id)}
                      className='h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary'
                    />
                  </div>

                  {/* Product Info */}
                  <div className='flex-1 flex items-center gap-3'>
                    <div className='w-16 h-16 md:w-20 md:h-20 flex-shrink-0 overflow-hidden rounded-md'>
                      <img
                        src={getProductImageUrl(item.image)}
                        alt={item.name}
                        className='w-full h-full object-cover'
                        onError={handleProductImageError}
                      />
                    </div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2'>
                        <h3 className='text-xs md:text-sm font-medium text-gray-900 line-clamp-2'>
                          {item.name}
                        </h3>
                        {item.type === 'fruit_box' && (
                          <Tag color='success' className='text-xs flex-shrink-0'>
                            <GiftOutlined /> Hộp quà
                          </Tag>
                        )}
                      </div>
                      <p className='mt-1 text-xs md:text-sm text-gray-500'>
                        {item.weight || item.unit}
                      </p>
                      <p className='mt-1 text-xs md:text-sm font-medium text-gray-900'>
                        {item.price?.toLocaleString('vi-VN')}₫
                      </p>
                    </div>
                  </div>

                  {/* Quantity */}
                  <div
                    className='flex-shrink-0'
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className='flex items-center'>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateItemQuantity(item.id, item.quantity - 1);
                        }}
                        className='text-gray-500 hover:text-primary focus:outline-none p-1'
                        disabled={item.quantity <= 1}
                      >
                        <MinusIcon className='h-3 w-3 md:h-4 md:w-4' />
                      </button>
                      <input
                        type='number'
                        value={item.quantity}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateItemQuantity(item.id, parseInt(e.target.value));
                        }}
                        min='1'
                        max={item.stock}
                        className='mx-1 md:mx-2 w-12 md:w-16 text-center border border-gray-300 rounded-md focus:ring-primary focus:border-primary text-xs md:text-sm'
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateItemQuantity(item.id, item.quantity + 1);
                        }}
                        className='text-gray-500 hover:text-primary focus:outline-none p-1'
                        disabled={item.quantity >= item.stock}
                      >
                        <PlusIcon className='h-3 w-3 md:h-4 md:w-4' />
                      </button>
                    </div>
                  </div>

                  {/* Remove Button */}
                  <div
                    className='flex-shrink-0'
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeItem(item.id);
                      }}
                      className='text-red-500 hover:text-red-700 p-1'
                      aria-label='Xóa sản phẩm'
                    >
                      <TrashIcon className='h-4 w-4 md:h-5 md:w-5' />
                    </button>
                  </div>
                </div>

                {/* Fruit Box Details */}
                {item.type === 'fruit_box' && (
                  <Collapse
                    ghost
                    bordered={false}
                    className='bg-gray-50 rounded-md border border-gray-100 mt-3'
                    expandIcon={({ isActive }) =>
                      isActive ? (
                        <ChevronUpIcon className='h-4 w-4' />
                      ) : (
                        <ChevronDownIcon className='h-4 w-4' />
                      )
                    }
                  >
                    <Panel
                      header={
                        <div className='flex items-center text-primary'>
                          <TagOutlined className='mr-2' />
                          <span className='font-medium'>
                            Chi tiết hộp quà ({item.selectedItems?.length || 0}{' '}
                            loại trái cây)
                          </span>
                        </div>
                      }
                      key='1'
                      className='site-collapse-custom-panel'
                    >
                      {/* Price Breakdown */}
                      <div className='space-y-3 mb-4 p-3 bg-white rounded-md border border-gray-100'>
                        <div className='flex justify-between items-center'>
                          <Text className='text-gray-600'>Giá hộp:</Text>
                          <Text strong>
                            {(item as any).initial_price?.toLocaleString(
                              'vi-VN'
                            )}
                            ₫
                          </Text>
                        </div>
                        <div className='flex justify-between items-center'>
                          <Text className='text-gray-600'>Giá trái cây:</Text>
                          <Text strong type='danger'>
                            {(item as any).fruits_price?.toLocaleString(
                              'vi-VN'
                            )}
                            ₫
                          </Text>
                        </div>
                        <Divider className='my-2' />
                        <div className='flex justify-between items-center'>
                          <Text strong>Tổng cộng:</Text>
                          <Text strong type='success'>
                            {item.price.toLocaleString('vi-VN')}₫
                          </Text>
                        </div>
                      </div>

                      {/* Selected Fruits List */}
                      <List
                        size='small'
                        dataSource={item.selectedItems}
                        className='bg-white rounded-md border border-gray-100'
                        renderItem={(selected) => (
                          <List.Item className='px-3 py-2 border-b border-dashed border-gray-100'>
                            <div className='flex justify-between w-full items-center'>
                              <div className='flex items-center'>
                                <ShoppingOutlined className='mr-2 text-gray-400' />
                                <Text>{selected.name}</Text>
                              </div>
                              <div className='flex items-center gap-2'>
                                <Tag color='blue'>
                                  {selected.quantity} ×{' '}
                                  {selected.price?.toLocaleString('vi-VN')}₫
                                </Tag>
                                <Text strong type='danger'>
                                  ={' '}
                                  {(
                                    selected.quantity * (selected.price || 0)
                                  ).toLocaleString('vi-VN')}
                                  ₫
                                </Text>
                              </div>
                            </div>
                          </List.Item>
                        )}
                      />

                      {/* Weight Info */}
                      <div className='mt-3 p-3 bg-white rounded-md border border-gray-100'>
                        <div className='flex justify-between items-center'>
                          <div className='flex items-center'>
                            <ScaleIcon className='h-4 w-4 mr-2 text-gray-500' />
                            <Text strong>Tổng trọng lượng:</Text>
                          </div>
                          <Tag color='processing'>{item.totalWeight}</Tag>
                        </div>
                      </div>
                    </Panel>
                  </Collapse>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Order Summary - Hidden on Mobile */}
        <div className='lg:col-span-1 hidden md:block'>
          <div className='bg-white rounded-lg shadow-sm p-6 sticky top-20'>
            <h2 className='text-lg font-medium mb-6'>Tóm tắt đơn hàng</h2>

            <div className='space-y-4 text-sm'>
              {/* Selected items count */}
              <div className='flex justify-between font-medium text-gray-700'>
                <span>Sản phẩm đã chọn</span>
                <span>
                  {selectedItems.length}/{items.length}
                </span>
              </div>

              {/* Subtotal */}
              <div className='flex justify-between'>
                <span className='text-gray-600'>Tổng tiền sản phẩm</span>
                <span className='font-medium'>
                  {selectedItems.length > 0
                    ? selectedSubtotal.toLocaleString('vi-VN')
                    : '0'}
                  ₫
                </span>
              </div>

              {/* Shipping */}
              <div className='flex justify-between'>
                <span className='text-gray-600'>Phí giao hàng</span>
                <span className='font-medium text-gray-600 italic'>
                  Sẽ được thông báo sau
                </span>
              </div>

              {/* Coupon/Promo code */}
              <div>
                {showCouponInput ? (
                  <div className='flex space-x-2 mb-2'>
                    <input
                      type='text'
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      placeholder='Mã giảm giá'
                      className='flex-1 p-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary text-sm'
                    />
                    <button
                      className='px-3 py-2 bg-primary text-white rounded-md text-sm'
                      onClick={() => applyPromoCode(couponCode)}
                      disabled={isProcessing}
                    >
                      {isProcessing ? 'Đang áp dụng...' : 'Áp dụng'}
                    </button>
                  </div>
                ) : (
                  <button
                    className='text-primary hover:text-primary-dark font-medium text-sm'
                    onClick={() => setShowCouponInput(true)}
                  >
                    + Thêm mã giảm giá
                  </button>
                )}

                {/* Available Promotions */}
                {promotions.length > 0 && (
                  <div className='mt-4'>
                    <h3 className='text-sm font-medium mb-2'>
                      Mã giảm giá hiện có:
                    </h3>
                    <div className='space-y-2 max-h-40 overflow-y-auto pr-1'>
                      {promotions.map((promo) => (
                        <div
                          key={promo.id}
                          className='border border-dashed border-primary rounded-md p-2 bg-primary-50 hover:bg-primary-100 cursor-pointer transition'
                          onClick={() => {
                            setCouponCode(promo.code);
                            setShowCouponInput(true);
                            applyPromoCode(promo.code);
                          }}
                        >
                          <div className='flex justify-between items-center'>
                            <span className='font-bold text-primary'>
                              {promo.code}
                            </span>
                            <button
                              className='text-xs bg-primary text-white px-2 py-1 rounded'
                              onClick={(e) => {
                                e.stopPropagation();
                                setCouponCode(promo.code);
                                applyPromoCode(promo.code);
                              }}
                            >
                              Áp dụng
                            </button>
                          </div>
                          <p className='text-xs text-gray-700'>{promo.name}</p>
                          <div className='flex justify-between text-xs text-gray-500 mt-1'>
                            <span>
                              Giảm:{' '}
                              {promo.type === 'percentage'
                                ? `${promo.value}%`
                                : `${promo.value.toLocaleString('vi-VN')}₫`}
                            </span>
                            <span>
                              Còn lại:{' '}
                              {promo.quantity.total - promo.quantity.used}/
                              {promo.quantity.total}
                            </span>
                          </div>
                          {promo.constraints.minimum_order_amount > 0 && (
                            <div className='text-xs text-orange-500 mt-1'>
                              Đơn tối thiểu:{' '}
                              {promo.constraints.minimum_order_amount.toLocaleString(
                                'vi-VN'
                              )}
                              ₫
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {loadingPromotions && (
                  <div className='text-center py-2'>
                    <span className='text-sm text-gray-500'>
                      Đang tải mã giảm giá...
                    </span>
                  </div>
                )}
              </div>

              {/* Discount */}
              {discount > 0 && (
                <div className='flex justify-between text-green-600'>
                  <span>Giảm giá</span>
                  <span className='font-medium'>
                    -{discount.toLocaleString('vi-VN')}₫
                  </span>
                </div>
              )}

              {/* Total */}
              <div className='flex justify-between pt-4 border-t border-gray-200'>
                <span className='font-medium'>Tổng thanh toán</span>
                <span className='text-lg font-bold text-primary'>
                  {selectedItems.length > 0
                    ? (selectedSubtotal - discount).toLocaleString('vi-VN')
                    : '0'}
                  ₫
                </span>
              </div>
            </div>

            {/* Checkout button - disabled if no items selected */}
            <button
              onClick={handleCheckout}
              className={`w-full mt-6 ${
                selectedItems.length > 0
                  ? 'bg-primary hover:bg-primary-dark'
                  : 'bg-gray-300 cursor-not-allowed'
              } text-white font-medium py-3 px-4 rounded-lg transition duration-300 flex items-center justify-center`}
              disabled={selectedItems.length === 0 || isProcessing}
            >
              <ShoppingCartIcon className='h-5 w-5 mr-2' />
              {isProcessing
                ? 'Đang xử lý...'
                : selectedItems.length > 0
                ? 'Xác nhận đặt hàng'
                : 'Chọn sản phẩm để thanh toán'}
            </button>

            {/* Add check all button */}
            <div className='flex justify-between items-center mt-4'>
              <button
                onClick={selectAllItems}
                className='text-primary hover:text-primary-dark text-sm font-medium flex items-center'
              >
                <CheckIcon className='h-4 w-4 mr-1' />
                Chọn tất cả
              </button>
              <button
                onClick={unselectAllItems}
                className='text-gray-500 hover:text-gray-700 text-sm font-medium'
              >
                Bỏ chọn tất cả
              </button>
            </div>

            {/* Continue shopping */}
            <div className='mt-6 text-center'>
              <Link
                to='/'
                className='text-primary hover:text-primary-dark text-sm font-medium'
              >
                ← Tiếp tục mua sắm
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Summary */}
      <div className='md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50'>
        <div className='p-4'>
          {/* Summary Info */}
          <div className='flex justify-between items-center mb-3'>
            <div className='flex items-center space-x-2'>
              <input
                type='checkbox'
                checked={isAllSelected}
                onChange={() =>
                  isAllSelected ? unselectAllItems() : selectAllItems()
                }
                className='h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary'
              />
              <span className='text-sm text-gray-600'>
                Đã chọn {selectedItems.length}/{items.length} sản phẩm
              </span>
            </div>
            <div className='text-right'>
              <div className='text-lg font-bold text-primary'>
                {selectedItems.length > 0
                  ? (selectedSubtotal - discount).toLocaleString('vi-VN')
                  : '0'}
                ₫
              </div>
              {discount > 0 && (
                <div className='text-xs text-green-600'>
                  Đã giảm {discount.toLocaleString('vi-VN')}₫
                </div>
              )}
            </div>
          </div>
          
          {/* Checkout Button */}
          <button
            onClick={handleCheckout}
            className={`w-full ${
              selectedItems.length > 0
                ? 'bg-primary hover:bg-primary-dark'
                : 'bg-gray-300 cursor-not-allowed'
            } text-white font-medium py-3 px-4 rounded-lg transition duration-300 flex items-center justify-center`}
            disabled={selectedItems.length === 0 || isProcessing}
          >
            <ShoppingCartIcon className='h-5 w-5 mr-2' />
            {isProcessing
              ? 'Đang xử lý...'
              : selectedItems.length > 0
              ? 'Mua hàng'
              : 'Chọn sản phẩm'}
          </button>
        </div>
      </div>

      {/* Checkout Form Modal */}
      <Modal
        title={t('cart.shippingInformation')}
        open={showCheckoutForm}
        onCancel={() => !isProcessing && setShowCheckoutForm(false)}
        footer={null}
        maskClosable={!isProcessing}
        closable={!isProcessing}
        width={600}
      >
        <Form
          form={form}
          layout='vertical'
          onFinish={submitCheckout}
          className='mt-4'
          initialValues={{ type: 'immediate' }}
        >
          <h3 className='font-medium mb-4 text-gray-800'>
            {t('cart.recipientInformation')}
          </h3>

          {/* Receiver Type Selection */}
          <div className='mb-6'>
            <label className='block text-sm font-medium text-gray-700 mb-3'>
              Người nhận hàng:
            </label>
            <div className='space-y-3'>
              <label className='flex items-center cursor-pointer'>
                <input
                  type='radio'
                  name='receiverType'
                  value='self'
                  checked={receiverType === 'self'}
                  onChange={(e) => setReceiverType(e.target.value as 'self' | 'other')}
                  className='h-4 w-4 text-primary focus:ring-primary border-gray-300'
                />
                <span className='ml-3 text-sm text-gray-700'>
                  Tôi sẽ nhận hàng
                </span>
              </label>
              <label className='flex items-center cursor-pointer'>
                <input
                  type='radio'
                  name='receiverType'
                  value='other'
                  checked={receiverType === 'other'}
                  onChange={(e) => setReceiverType(e.target.value as 'self' | 'other')}
                  className='h-4 w-4 text-primary focus:ring-primary border-gray-300'
                />
                <span className='ml-3 text-sm text-gray-700'>
                  Người khác sẽ nhận hàng
                </span>
              </label>
            </div>
          </div>


          {/* Thông tin người đặt */}
          <div className='mb-6'>
            <h4 className='text-base font-bold text-gray-800 mb-4 border-b-2 border-gray-300 pb-3'>
              Thông tin người đặt
            </h4>
            <Form.Item
              name='order_phone'
              label={t('cart.orderPhone')}
              rules={[
                {
                  required: true,
                  message: t('cart.requiredOrderPhone'),
                },
                { pattern: /^[0-9]{10}$/, message: t('cart.invalidPhone') },
              ]}
            >
              <Input
                prefix={<PhoneIcon className='h-5 w-5 text-gray-400 mr-2' />}
                placeholder={t('cart.phonePlaceholder')}
                disabled={isProcessing}
              />
            </Form.Item>
          </div>

          {/* Thông tin người nhận */}
          <div className='mb-6'>
            <h4 className='text-base font-bold text-gray-800 mb-4 border-b-2 border-gray-300 pb-3'>
              Thông tin người nhận
            </h4>
            <Form.Item
              name='name'
              label={t('cart.recipientName')}
              rules={[
                { required: true, message: t('cart.requiredRecipientName') },
              ]}
            >
              <Input
                prefix={<UserIcon className='h-5 w-5 text-gray-400 mr-2' />}
                placeholder={t('cart.recipientNamePlaceholder')}
                disabled={isProcessing}
              />
            </Form.Item>

            {receiverType === 'other' && (
              <Form.Item
                name='phone'
                label={t('cart.recipientPhone')}
                rules={[
                  { required: receiverType === 'other', message: 'Vui lòng nhập số điện thoại người nhận' },
                  { pattern: /^[0-9]{10}$/, message: t('cart.invalidPhone') },
                ]}
              >
                <Input
                  prefix={<PhoneIcon className='h-5 w-5 text-gray-400 mr-2' />}
                  placeholder={t('cart.phonePlaceholder')}
                  disabled={isProcessing}
                />
              </Form.Item>
            )}
          </div>

          <Form.Item
            name='address'
            label={t('cart.shippingAddress')}
            rules={[{ required: true, message: t('cart.requiredAddress') }]}
          >
            <Input.TextArea
              placeholder={t('cart.addressPlaceholder')}
              rows={3}
              disabled={isProcessing}
            />
          </Form.Item>

          <Form.Item name='deliveryNote' label='Ghi chú đơn hàng'>
            <Input.TextArea
              placeholder='Nhập ghi chú cho đơn hàng của bạn...'
              rows={3}
              disabled={isProcessing}
            />
          </Form.Item>

          <h3 className='font-medium mb-4 mt-6 text-gray-800'>
            Thời gian giao hàng
          </h3>

          <Form.Item name='type'>
            <Radio.Group
              onChange={(e) => setType(e.target.value)}
              value={type}
              disabled={isProcessing}
            >
              <Radio value='immediate' className='flex items-start mb-3'>
                <div>
                  <div className='font-medium flex items-center'>
                    <ClockIcon className='h-5 w-5 mr-2 text-primary' />
                    Giao hàng ngay khi có thể
                  </div>
                  <div className='text-sm text-gray-500 ml-7'>
                    Đơn hàng sẽ được giao trong thời gian sớm nhất
                  </div>
                </div>
              </Radio>
              <Radio value='schedule' className='flex items-start mb-3'>
                <div>
                  <div className='font-medium flex items-center'>
                    <CalendarIcon className='h-5 w-5 mr-2 text-primary' />
                    Đặt lịch giao hàng
                  </div>
                  <div className='text-sm text-gray-500 ml-7'>
                    Chọn ngày và giờ cụ thể để giao hàng
                  </div>
                </div>
              </Radio>
              <Radio value='pickup' className='flex items-start'>
                <div>
                  <div className='font-medium flex items-center'>
                    <ShoppingCartIcon className='h-5 w-5 mr-2 text-primary' />
                    Nhận hàng tại cửa hàng
                  </div>
                  <div className='text-sm text-gray-500 ml-7'>
                    Chọn thời gian nhận hàng tại cửa hàng
                  </div>
                </div>
              </Radio>
            </Radio.Group>
          </Form.Item>

          {(type === 'schedule' || type === 'pickup') && (
            <div className='ml-7 mb-4'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <Form.Item
                  name='deliveryDate'
                  label='Ngày nhận hàng'
                  rules={[
                    {
                      required: type === 'schedule' || type === 'pickup',
                      message: 'Vui lòng chọn ngày nhận hàng',
                    },
                  ]}
                >
                  <DatePicker
                    format='DD/MM/YYYY'
                    className='w-full'
                    placeholder='Chọn ngày'
                    disabled={isProcessing}
                    locale={locale}
                    disabledDate={(current) => {
                      // Không cho phép chọn ngày trong quá khứ
                      return (
                        current &&
                        current.valueOf() < Date.now() - 24 * 60 * 60 * 1000
                      );
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name='deliveryTime'
                  label='Thời gian'
                  rules={[
                    {
                      required: type === 'schedule' || type === 'pickup',
                      message: 'Vui lòng chọn giờ nhận hàng',
                    },
                  ]}
                >
                  <TimePicker
                    format='HH:mm'
                    className='w-full'
                    placeholder='Chọn giờ'
                    disabled={isProcessing}
                    minuteStep={30}
                    hideDisabledOptions
                    disabledHours={() => [0, 1, 2, 3, 4, 5, 6, 22, 23]}
                  />
                </Form.Item>
              </div>
            </div>
          )}

          <Form.Item>
            <div className='flex justify-end space-x-4'>
              <button
                type='button'
                onClick={() => setShowCheckoutForm(false)}
                className='px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50'
                disabled={isProcessing}
              >
                Hủy
              </button>
              <button
                type='submit'
                className='px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark flex items-center'
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <span className='animate-spin mr-2'>⏳</span>
                    Đang xử lý...
                  </>
                ) : (
                  <>Hoàn tất đặt hàng</>
                )}
              </button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CartPage;
